/**
 * StickyMobileCTA - Fixed bottom CTA button for mobile
 *
 * Features:
 * - Fixed at bottom of viewport on mobile only
 * - Only visible when the main submit button is scrolled out of view
 * - Enabled: scrolls to payment section
 * - Disabled: scrolls to next unfilled field
 * - Hidden on desktop
 */

interface StickyMobileCTAProps {
  /** Whether the form is ready to submit */
  isEnabled?: boolean;
  /** Callback when button is clicked */
  onClick?: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Custom button text */
  children?: React.ReactNode;
  /** Whether the sticky CTA should be visible (main button scrolled out of view) */
  isVisible?: boolean;
}

export function StickyMobileCTA({
  isEnabled = false,
  onClick,
  isSubmitting = false,
  children,
  isVisible = true,
}: StickyMobileCTAProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white p-4 md:hidden">
      <button
        type="button"
        onClick={handleClick}
        disabled={isSubmitting}
        className={`w-full min-h-[44px] rounded-full p-[16px_24px] text-base font-semibold transition-all ${
          isEnabled
            ? 'bg-catch-avocado-400 text-catch-dark-blue-90 hover:brightness-95'
            : 'bg-slate-200 text-slate-500'
        } disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <svg
              className="h-5 w-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : (
          children || 'Start my risk assessment'
        )}
      </button>
    </div>
  );
}

export default StickyMobileCTA;
